<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header class="flex items-end justify-between">
      <div>
        <h2 class="text-4xl font-black tracking-tight">
          {{ campaign.name }}
        </h2>
        <p class="text-slate-400 mt-2">
          {{ campaign.objective }} · {{ campaign.industry }}
        </p>
      </div>

      <span class="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider"
        :class="statusClass(campaign.status)">
        {{ campaign.status }}
      </span>
    </header>

    <!-- KPIs -->
    <section class="grid grid-cols-1 md:grid-cols-4 gap-8">
      <div v-for="kpi in kpis" :key="kpi.label" class="bg-white p-8 rounded-3xl premium-shadow
               border border-slate-50">
        <p class="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">
          {{ kpi.label }}
        </p>
        <h3 class="text-4xl font-black mt-2">
          {{ kpi.value }}
          <span v-if="kpi.suffix" class="text-lg opacity-30">
            {{ kpi.suffix }}
          </span>
        </h3>
      </div>
    </section>

    <!-- MAIN GRID -->
    <section class="grid grid-cols-1 lg:grid-cols-12 gap-12">

      <!-- LEFT -->
      <div class="lg:col-span-8 space-y-8">

        <!-- CAMPAIGN OVERVIEW -->
        <div class="bg-white p-8 rounded-[2.5rem] premium-shadow
                 border border-slate-50">
          <h3 class="text-xl font-bold mb-6">
            Campaign Overview
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <p class="label">Objective</p>
              <strong>{{ campaign.objective }}</strong>
            </div>

            <div>
              <p class="label">Timeline</p>
              <strong>
                {{ campaign.startDate }} → {{ campaign.endDate }}
              </strong>
            </div>

            <div>
              <p class="label">Primary Interests</p>
              <strong>{{ campaign.audience.interests }}</strong>
            </div>

            <div>
              <p class="label">Target Region</p>
              <strong>{{ campaign.audience.region }}</strong>
            </div>
          </div>

          <p class="text-sm text-slate-500 mt-6">
            {{ campaign.description }}
          </p>
        </div>

        <!-- INFLUENCERS -->
        <div class="bg-white p-8 rounded-[2.5rem] premium-shadow
                 border border-slate-50">
          <h3 class="text-xl font-bold mb-6">
            Selected Influencers
          </h3>

          <div class="space-y-4">
            <div v-for="i in influencers" :key="i.id" class="flex items-center justify-between
                     p-5 rounded-xl bg-slate-50/50">
              <!-- LEFT -->
              <div>
                <p class="font-bold text-slate-900">
                  {{ i.user_name }}
                </p>
                <p class="text-xs text-slate-400">
                  {{ i.user_email }}
                </p>
              </div>

              <!-- RIGHT -->
              <div class="text-right text-sm">
                <p>
                  Risk Score: <strong>{{ i.cinf_risk_score }}</strong>
                </p>
                <p class="text-xs text-slate-400">
                  Fit score {{ i.cinf_fit_score }}/10
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- RIGHT -->
      <div class="lg:col-span-4 space-y-8">

        <!-- FIT SUMMARY -->
        <div class="bg-white p-8 rounded-[2rem] premium-shadow
                 border border-slate-50">
          <h3 class="text-xl font-bold mb-6">
            Campaign Fit Summary
          </h3>

          <ul class="space-y-4 text-sm">
            <li class="flex justify-between">
              <span class="text-slate-400">Audience Match</span>
              <strong>{{ fit.audience }}%</strong>
            </li>
            <li class="flex justify-between">
              <span class="text-slate-400">Avg Engagement</span>
              <strong>{{ fit.engagement }}%</strong>
            </li>
            <li class="flex justify-between">
              <span class="text-slate-400">Risk Flags</span>
              <strong :class="fit.risk === 'Low' ? 'text-emerald-600' : 'text-amber-600'">
                {{ fit.risk }}
              </strong>
            </li>
          </ul>
        </div>

        <!-- READINESS -->
        <!-- <div
          class="bg-white p-8 rounded-[2rem] premium-shadow
                 border border-slate-50"
        >
          <h3 class="text-xl font-bold mb-6">
            Readiness Checklist
          </h3>

          <ul class="space-y-3 text-sm">
            <li v-for="c in readiness" :key="c.label" class="flex items-center gap-3">
              <span
                class="size-3 rounded-full"
                :class="c.ok ? 'bg-emerald-500' : 'bg-slate-300'"
              ></span>
              <span>{{ c.label }}</span>
            </li>
          </ul>
        </div> -->

      </div>
    </section>

    <!-- FOOTER ACTIONS -->
    <section class="flex justify-end gap-4 pt-6">
      <button @click="exportSummary" class="border border-slate-200 px-8 py-3 rounded-xl
               text-xs font-bold uppercase tracking-wider
               hover:bg-slate-50 transition">
        Export Summary
      </button>

      <button v-if="campaign.status === 'Draft'" @click="markReadyNow()" class="bg-primary text-white px-10 py-3 rounded-xl
               text-xs font-bold uppercase tracking-wider
               hover:opacity-90 transition">
        Mark as Ready
      </button>
    </section>

  </section>
</template>

<script>
  import Toast, { POSITION } from "vue-toastification";
  import "vue-toastification/dist/index.css";

  import { getCampaignDetailApi, updateCampaignStatusApi } from "../apis/agencampapis";

  export default {
    name: "CampaignsDetails",

    data() {
      return {
        campaign: {
          id: "",
          name: "",
          industry: "",
          objective: "",
          status: "",
          startDate: "",
          endDate: "",
          description: "",
          audience: {
            interests: "",
            region: ""
          }
        },

        kpis: [],
        influencers: [],
        fit: {
          audience: 0,
          engagement: 0,
          risk: "-"
        },
        readiness: [],

        user_id: ""
      };
    },

    async mounted() {

      try {
        const ldataRaw = sessionStorage.getItem("ldata");

        if (ldataRaw && ldataRaw !== "undefined") {
          const ldata = JSON.parse(ldataRaw);
          if (ldata && ldata.user_id) {
            this.user_id = ldata.user_id;
          }
        }
      } catch (e) {
        console.log("Session parse error", e);
      }

      await this.loadCampaign();
    },

    methods: {
      async markReadyNow() {

        const res = await updateCampaignStatusApi(this.campaign.id, "Ready");

        if (res.status) {

          Toast.success("Campaign marked as Ready");
          this.campaign.status = "Ready";

        } else {

          Toast.error("Failed to update status");
        }
      },
      exportSummary() {

        const summary = `
CAMPAIGN SUMMARY

Campaign: ${this.campaign.name}
Objective: ${this.campaign.objective}
Status: ${this.campaign.status}

Timeline:
${this.campaign.startDate} → ${this.campaign.endDate}

Audience Filters:
• Interests: ${this.campaign.audience.interests || "Any"}
• Region: ${this.campaign.audience.region || "Any"}

Selected Influencers:
${this.influencers.length
            ? this.influencers.map(i =>
              `• ${i.user_name} (${i.user_email}) – Fit: ${i.cinf_fit_score}/10`
            ).join("\n")
            : "No influencers selected"
          }

Generated by Platform
`;

        const blob = new Blob([summary], {
          type: "text/plain;charset=utf-8;"
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "campaign-summary.txt";
        link.click();
      },
      async loadCampaign() {

        const campId = this.$route.params.campId;

        if (!campId) {
          Toast.error("Invalid campaign", { position: POSITION.TOP_RIGHT });
          return;
        }

        const res = await getCampaignDetailApi(campId);

        if (res.status) {

          this.campaign = res.campaign;
          this.kpis = res.kpis || this.placeholderKpis();
          this.influencers = res.influencers || [];
          this.fit = res.fit || { audience: 0, engagement: 0, risk: "-" };
          this.readiness = res.readiness || [];

        } else {

          Toast.error("Failed to load campaign", {
            position: POSITION.TOP_RIGHT
          });

          this.applyPlaceholders();
        }
      },

      applyPlaceholders() {

        this.campaign = this.placeholderCampaign();
        this.kpis = this.placeholderKpis();
        this.influencers = [];
        this.fit = { audience: 0, engagement: 0, risk: "-" };
        this.readiness = [];
      },



      placeholderKpis() {
        return [
          { label: "Influencers", value: 0 },
          { label: "Estimated Reach", value: "-" },
          { label: "Avg Fit Score", value: "-", suffix: "/10" },
          { label: "Risk Level", value: "-" }
        ];
      },

      statusClass(status) {
        if (status === "Draft") return "bg-slate-100 text-slate-700";
        if (status === "Ready") return "bg-emerald-50 text-emerald-700";
        if (status === "Active") return "bg-blue-50 text-blue-700";
        return "bg-slate-50 text-slate-400";
      }
    }
  };
</script>


<style scoped>
  .label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: #94a3b8;
  }

  /* ===================================
   CAMPAIGN DETAILS – MOBILE PATCH
   =================================== */
  @media (max-width: 640px) {

    /* Page padding */
    .px-12 {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .py-12 {
      padding-top: 24px !important;
      padding-bottom: 32px !important;
    }

    /* Header */
    header.flex {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    header h2 {
      font-size: 26px !important;
      line-height: 1.2;
    }

    header p {
      font-size: 14px;
    }

    header span {
      align-self: flex-start;
    }

    /* KPI cards */
    .rounded-3xl {
      padding: 20px !important;
      border-radius: 20px !important;
    }

    .text-4xl {
      font-size: 28px !important;
    }

    /* Main grid spacing */
    .gap-12 {
      gap: 24px !important;
    }

    /* Card containers */
    .rounded-\\[2\\.5rem\\],
    .rounded-\\[2rem\\] {
      padding: 20px !important;
      border-radius: 20px !important;
    }

    /* Overview grid → stack tighter */
    .md\\:grid-cols-2 {
      grid-template-columns: 1fr !important;
    }

    /* Influencer rows */
    .p-5 {
      padding: 14px !important;
    }

    .flex.items-center.justify-between {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .flex.items-center.justify-between .text-right {
      text-align: left;
    }

    /* Fit + readiness cards */
    ul.space-y-4 li,
    ul.space-y-3 li {
      font-size: 13px;
    }

    /* Footer actions */
    section.flex.justify-end {
      flex-direction: column;
      gap: 12px;
    }

    section.flex.justify-end button {
      width: 100%;
      text-align: center;
    }

    /* Buttons */
    .px-8.py-3,
    .px-10.py-3 {
      padding: 14px 16px !important;
      font-size: 11px;
    }
  }
</style>